import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { ParamsClean } from '@/common/decorators/params-clean'

/** 未修复原因 API 字段 → i18n 键（examinationStatus.*） */
export const EXAM_REASON_I18N_KEY: Record<string, string> = {
  'speed.start': 'examinationStatus.bootSpeed',
  'speed.network': 'examinationStatus.networkSpeed',
  'speed.system': 'examinationStatus.systemSpeed',
  'system.network': 'examinationStatus.networkSetting',
  'system.browser': 'examinationStatus.browserSafe',
  'system.problem': 'examinationStatus.systemIssue',
  'virus.system': 'examinationStatus.systemSafe',
  'virus.mobile_devices': 'examinationStatus.mobileDevice',
  'garbage.game': 'examinationStatus.gameJunk',
  'garbage.register': 'examinationStatus.registryJunk',
  'garbage.useless': 'examinationStatus.unusedSoft',
  'garbage.movie': 'examinationStatus.mediaJunk',
  'garbage.network': 'examinationStatus.webJunk',
  'garbage.system': 'examinationStatus.systemJunk',
  'garbage.talk': 'examinationStatus.chatJunk',
  'garbage.uninstall': 'examinationStatus.uninstallResidual',
  'garbage.other': 'examinationStatus.otherJunk',
  'guard.system': 'examinationStatus.systemProtect',
  'guard.shop': 'examinationStatus.shopProtect',
  'guard.homepage': 'examinationStatus.homepageProtect',
  'softpromotion': 'examinationStatus.softClean',
}

/** 请求参数 */
export interface ExaminationSummaryParams {
  /** 分组id，0-全部分组 */
  group_id: number
  /** 系统类型，0-全部，1-windows，2-linux，3-server */
  system_type?: number
}

/** 分数分布 */
export interface ScoreDistribution {
  /** 100分数量 */
  count_100: number
  /** 60-99分数量 */
  count_60_99: number
  /** 60分以下数量 */
  count_below_60: number
}

/** 未修复原因项 */
export interface UnfixedReasonTop {
  /** 原因名称 */
  reason: string
  /** 出现次数 */
  count: number
}

/** 体检汇总响应数据 */
export interface ExaminationSummaryData {
  /** 已体检终端数 */
  examined_count: number
  /** 近7天未体检终端数 */
  not_exam_count: number
  /** 低分主机数（分数<60） */
  low_score_count: number
  /** 体检合格率（百分比），仅统计 windows+server 终端 */
  qualified_rate: number
  /** 分数分布 */
  score_distribution: ScoreDistribution
  /** 未修复原因TOP5 */
  unfixed_reason_top: UnfixedReasonTop[]
}

/** 体检统计卡片数据 */
export interface ExamStatisticData {
  /** 近7天已体检终端 */
  examined_count: number
  /** 近7天未体检终端 */
  unexamined_count: number
  /** 60分以下的主机 */
  substandard_count: number
  /** 体检合格率 */
  pass_rate: number
}

/** 体检分数分布数据 */
export interface ExamScoreDistribution {
  /** 100分 */
  perfect_count: number
  /** 60-99分 */
  standard_count: number
  /** 60分以下 */
  substandard_count: number
}

/** 未修复原因项 */
export interface UnrepairedReasonItem {
  name: string
  value: number
}

export interface FetchOptions {
  group_id: number // 分组ID
  key_word: string // 关键词搜索
  page: number // 页码
  limit: number // 每页数量
  sort: string // 排序字段名
  asc: boolean // true-正序，false-逆序
  is_exam?: number // 0-全部,1-体检，2-未体检
  system_type?: number // 系统类型
  login_status?: number // 0-全部,1-在线，2-离线
  score_range?: number // 分数范围，0-全部,1-60以下，2-60~99，3-100
  /** 未修复原因（与汇总 unfixed_reason_top.reason 一致，如 garbage.game） */
  reason?: string
}

export interface ExaminationItem {
  device_id: string // 终端id
  group_id: number // 分组id
  group_name: string // 分组名
  computer_name: string // 计算机名
  user_mark: string // 登记用户名
  client_ip: string // 外网IP
  system_ip: string // 内网IP
  mac: string // Mac地址
  examination_time: string // 体检时间，时间戳(秒)
  score: number // 体检分数
  is_exam: boolean // 是否已体检
  exam_status?: number // 体检进度
  login_status?: number // 在线状态 1-在线 2-离线
  system_type?: number // 系统类型 1-windows 2-linux
  physical_exam_status?: number // 体检状态 1-未体检 2-体检中 3-体检完成 4-体检超时
}

export interface ResultItem {
  /** 终端id */
  device_id: string
  /** 最近体检时间，时间戳(秒) */
  examination_time: number
  /** 体检得分 */
  score: number
  /** 体检状态 */
  physical_exam_status: number
}

export interface OrderOption {
  execute_type: number
  device_list: string[]
  group_id: number
  is_repair: boolean
}

@Injectable()
export class ExaminationService implements ExaminationDetailService {
  constructor (private http: Http) {}

  @ParamsClean()
  getList (options: FetchOptions) {
    return this.http
      .post<{
        total: number,
        list: ExaminationItem[]
      }>('safemonitor/examination/stat', options)
  }

  getExaminationResult (options: { device_ids: string[] }) {
    return this.http.post<{
      list: ResultItem[]
    }>('safemonitor/physical-exam-result', options)
  }

  @InvokeThrottle()
  fetchExaminationDetails (device_id: string) {
    return this.http
      .post<{
        exam_detail: string
      }>('safemonitor/examination/info', { device_id: device_id }).then(res => {
        const resultObj = JSON.parse(res.exam_detail)
        Object.keys(resultObj).forEach(key => {
          let count = 0
          Object.keys(resultObj[key]).forEach(subKey => {
            count += resultObj[key][subKey]
          })
          resultObj[key].count = count
        })
        return resultObj
      })
  }

  @InvokeThrottle()
  repairTerminal (option: OrderOption) {
    return this.http
      .post('/terminal-order/repair', option)
  }

  /** 获取体检汇总数据（包含统计卡片、分数分布、未修复原因） */
  @InvokeThrottle()
  getExaminationSummary (params: ExaminationSummaryParams): Promise<ExaminationSummaryData> {
    return this.http.post<ExaminationSummaryData>('/safemonitor/examination/summary', params)
  }
}

// 实现 ExaminationDetailService 接口
export interface ExaminationDetailService {
  fetchExaminationDetails(device_id: string): Promise<any>
}
